import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from './store/authStore';
import { useThemeStore } from './store/themeStore';
import { wsService } from './services/websocket';

// Components
import Navbar from './components/Navbar';
import LoadingSpinner from './components/LoadingSpinner';
import I18nProvider from './components/I18nProvider';

// Pages
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import SessionPage from './pages/SessionPage';
import StatisticsPage from './pages/StatisticsPage';
import SettingsPage from './pages/SettingsPage';
import OBSPage from './pages/OBSPage';
import AdminPage from './pages/AdminPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import ImprintPage from './pages/ImprintPage';

function AppContent() {
  const { isAuthenticated, isLoading, user, checkAuth } = useAuthStore();
  const { initializeTheme } = useThemeStore();
  const location = useLocation();
  
  // Check if we're on the OBS page
  const isOBSPage = location.pathname.startsWith('/obs');

  useEffect(() => {
    checkAuth();
    initializeTheme();
  }, [checkAuth, initializeTheme]); // Added missing dependencies

  useEffect(() => {
    if (isAuthenticated && user) {
      // Connect to WebSocket when authenticated
      wsService.connect(user.id);
      
      return () => {
        wsService.disconnect();
      };
    }
  }, [isAuthenticated, user]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 light:bg-gray-50 text-white light:text-gray-900 flex flex-col">
      <Toaster
        position="bottom-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'var(--toast-bg)',
            color: 'var(--toast-color)',
            border: '1px solid var(--toast-border)',
          },
        }}
      />
      
      {isAuthenticated && !isOBSPage && <Navbar />}
      
      <main className={`flex-1 flex flex-col ${isAuthenticated && !isOBSPage ? 'pt-16' : ''}`}>
        <Routes>
          {/* Public routes */}
          <Route 
            path="/" 
            element={isAuthenticated ? <Navigate to="/dashboard" /> : <HomePage />} 
          />
          <Route 
            path="/login" 
            element={isAuthenticated ? <Navigate to="/dashboard" /> : <LoginPage />} 
          />
          
          {/* Legal pages (accessible to everyone) */}
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/imprint" element={<ImprintPage />} />
          
          {/* Protected routes */}
          <Route 
            path="/dashboard" 
            element={isAuthenticated ? <DashboardPage /> : <Navigate to="/" />} 
          />
          <Route 
            path="/session/:id" 
            element={isAuthenticated ? <SessionPage /> : <Navigate to="/" />} 
          />
          <Route 
            path="/statistics" 
            element={isAuthenticated ? <StatisticsPage /> : <Navigate to="/" />} 
          />
          <Route 
            path="/settings" 
            element={isAuthenticated ? <SettingsPage /> : <Navigate to="/" />} 
          />
          
          {/* OBS route (can be accessed without auth for overlay purposes) */}
          <Route path="/obs" element={<OBSPage />} />
          <Route path="/obs/:sessionId" element={<OBSPage />} />
          
          {/* Admin route (protected, admin only) */}
          <Route 
            path="/admin" 
            element={isAuthenticated ? <AdminPage /> : <Navigate to="/" />} 
          />
          
          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <I18nProvider>
      <Router>
        <AppContent />
      </Router>
    </I18nProvider>
  );
}

export default App; 